import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import CompaniesSettingsService from 'api/services/CompaniesSettingsService';
import _ from 'lodash';
import { RootState } from 'store/store';
import { PLATFORMS } from 'store/types';
import { ACTIVITIES } from 'types';

export const fetchKpis = createAsyncThunk<any, void, { state: RootState }>('fetchKpis', async (_, { getState }) => {
    const companyId = getState().userState.me.data.company.id;
    const response = await CompaniesSettingsService.kpis({ companyId });
    return response.data;
});

export const fetchApis = createAsyncThunk<any, void, { state: RootState }>('fetchApis', async (_, { getState }) => {
    const companyId = getState().userState.me.data.company.id;
    const response = await CompaniesSettingsService.integrationsApis({ companyId });
    return response.data;
});

export const fetchCallouts = createAsyncThunk<any, void, { state: RootState }>(
    'fetchCallouts',
    async (_, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await CompaniesSettingsService.callouts({ companyId });
        return response.data;
    },
);

export type TKpis = {
    avgDU: number;
    callTime: number;
    companyId: string;
    convoRate: number;
    convoTime: number;
    dqRate: number;
    id: string;
    offerRate: number;
    pickUpRate: number;
    setRate: number;
    uniquePickUpRate: number;
};

export type FilteredKpis = Omit<TKpis, 'companyId' | 'id'>;

type TCompaniesSettingState = {
    kpis: {
        data: TKpis;
        isLoading: boolean;
    };
    apis: {
        data: Array<TApiData>;
        isLoading: boolean;
    };
    callouts: {
        data: { [key in ACTIVITIES]: string };
        isLoading: boolean;
    };
};

export type TApiData = {
    companyId: string;
    endpoints: Array<string>;
    id: string;
    key: string;
    name: PLATFORMS;
};

const initialState: TCompaniesSettingState = {
    kpis: {
        data: {} as TKpis,
        isLoading: false,
    },
    apis: {
        data: [],
        isLoading: false,
    },
    callouts: {
        data: {} as TCompaniesSettingState['callouts']['data'],
        isLoading: false,
    },
};

export const companiesSettingSlice = createSlice({
    name: 'companiesSetting',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchKpis.pending, state => {
                state.kpis.isLoading = true;
            })
            .addCase(fetchKpis.fulfilled, (state, action) => {
                state.kpis.data = action.payload;
                state.kpis.isLoading = false;
            });
        builder
            .addCase(fetchApis.pending, state => {
                state.apis.isLoading = true;
            })
            .addCase(fetchApis.fulfilled, (state, action) => {
                state.apis.data = action.payload;
                state.apis.isLoading = false;
            });
        builder
            .addCase(fetchCallouts.pending, state => {
                state.callouts.isLoading = true;
            })
            .addCase(fetchCallouts.fulfilled, (state, action) => {
                state.callouts.data = action.payload;
                state.callouts.isLoading = false;
            });
    },
});

export const selectKpisArray = createSelector([(state: RootState) => state.companiesSettingState.kpis], data => {
    const items = _.omit(data.data, ['companyId', 'id']) as FilteredKpis;

    return Object.entries(items).map(([key, value]) => ({ name: key, value })) as Array<{
        name: keyof FilteredKpis;
        value: any;
    }>;
});

export const selectKpisObject = createSelector([(state: RootState) => state.companiesSettingState.kpis], data => {
    return _.omit(data.data, ['companyId', 'id']) as FilteredKpis;
});

export const selectKpisIsLoading = createSelector(
    [(state: RootState) => state.companiesSettingState.kpis],
    data => data.isLoading,
);

export const selectApis = createSelector([(state: RootState) => state.companiesSettingState.apis], data => data.data);
export const selectApisIsLoading = createSelector(
    [(state: RootState) => state.companiesSettingState.apis],
    data => data.isLoading,
);

export const selectCallouts = createSelector(
    [(state: RootState) => state.companiesSettingState.callouts],
    data => data.data,
);
export const selectCalloutsArray = createSelector([(state: RootState) => state.companiesSettingState.callouts], data =>
    Object.entries(data.data as object).map(([key, value]) => ({
        name: key,
        value,
    })),
);

export const selectCalloutsIsLoading = createSelector(
    [(state: RootState) => state.companiesSettingState.callouts],
    data => data.isLoading,
);

export default companiesSettingSlice.reducer;
